import React from "react";
import {
  Grid,
  Box,
  Typography,
  Stack,
  Avatar,
  Badge,
  IconButton,
  Button,
  alpha,
  useTheme,
} from "@mui/material";
import { keyframes } from "@mui/system";
import {
  CameraAlt as CameraAltIcon,
  Edit as EditIcon,
  Payments as PaymentsIcon,
  CreditScore as CreditScoreIcon,
  Call as CallIcon,
  Mail as MailIcon,
  Sync as SyncIcon,
  PublishedWithChanges as PublishedWithChangesIcon,
} from "@mui/icons-material";
import moment from "moment";
import { styled } from "@mui/material/styles";
import { formatBytes } from "../../../utils/common";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SummaryCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "colorScheme",
})(({ theme, colorScheme = "primary" }) => {
  const mainColor = theme.palette[colorScheme]?.main || theme.palette.primary.main;
  const lightColor = theme.palette[colorScheme]?.light || mainColor;
  return {
    padding: theme.spacing(3),
    borderRadius: "28px",
    height: "100%",
    background: "white",
    border: `2px solid ${alpha(mainColor, 0.4)}`,
    boxShadow: `0 12px 40px ${alpha(mainColor, 0.15)}`,
    position: "relative",
    overflow: "hidden",
    animation: `${fadeInUp} 0.6s ease-out forwards`,
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      boxShadow: `0 16px 50px ${alpha(mainColor, 0.25)}`,
      border: `2px solid ${mainColor}`,
      transform: "translateY(-4px)",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "3px",
      background: `linear-gradient(90deg, ${mainColor} 0%, ${lightColor} 100%)`,
    },
  };
});

const SectionTitle = ({ title, icon: Icon, color = "primary", centered = false }) => {
  const theme = useTheme();
  const mainColor = theme.palette[color]?.main || theme.palette.primary.main;
  const darkColor = theme.palette[color]?.dark || theme.palette.primary.dark;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: centered ? "center" : "flex-start",
        gap: 2,
        mb: 3,
        p: 1.5,
        width: "100%",
        borderRadius: "12px",
        background: `linear-gradient(135deg, ${alpha(mainColor, 0.3)} 0%, ${alpha(mainColor, 0.1)} 100%)`,
        border: `1px solid ${alpha(mainColor, 0.4)}`,
        boxShadow: `0 4px 12px ${alpha(mainColor, 0.15)}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 38,
          height: 38,
          borderRadius: "10px",
          bgcolor: "white",
          boxShadow: `0 4px 10px ${alpha(mainColor, 0.2)}`,
        }}
      >
        <Icon sx={{ fontSize: "1.3rem", color: mainColor }} />
      </Box>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 900,
          letterSpacing: "0.02em",
          color: darkColor,
          fontSize: "0.95rem",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

const IDPill = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(0.8, 2),
  borderRadius: "50px",
  background: alpha(theme.palette.divider, 0.1),
  border: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
  marginTop: theme.spacing(2),
  "& .id-icon": {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    background: theme.palette.primary.main,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.65rem",
    fontWeight: 900,
  },
  "& .id-text": {
    fontWeight: 800,
    fontSize: "0.85rem",
    color: theme.palette.text.primary,
  },
}));

export const ProfileWidget = ({ user, profileImageUrl, handleAvatarClick }) => {
  const theme = useTheme();
  return (
    <SummaryCard
      colorScheme="primary"
      sx={{
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        badgeContent={
          <IconButton
            onClick={handleAvatarClick}
            size="small"
            sx={{
              bgcolor: "primary.main",
              color: "white",
              p: 0.5,
              border: "2px solid white",
              boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            <CameraAltIcon sx={{ fontSize: 10 }} />
          </IconButton>
        }
        sx={{ mb: 1.5 }}
      >
        <Avatar
          src={profileImageUrl}
          sx={{
            width: 64,
            height: 64,
            border: `3px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            p: 0.4,
            bgcolor: "white",
          }}
        />
      </Badge>

      <Typography variant="subtitle1" sx={{ fontWeight: 900, letterSpacing: "-0.01em", mb: 0.5 }}>
        {user.fullName || user.uname}
      </Typography>

      <IDPill sx={{ mb: 2, transform: "scale(0.9)" }}>
        <Box className="id-icon">ID</Box>
        <Box className="id-text">{user.uname}</Box>
      </IDPill>

      <Stack spacing={1.5} alignItems="center">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CallIcon sx={{ fontSize: 18, color: alpha(theme.palette.primary.main, 0.6) }} />
          <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary" }}>
            {user.mobileNo}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <MailIcon sx={{ fontSize: 18, color: alpha(theme.palette.primary.main, 0.6) }} />
          <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary" }}>
            {user.emailId}
          </Typography>
        </Box>
      </Stack>
    </SummaryCard>
  );
};

export const PlanWidget = ({ user, renewUser, permissionKeys, operator }) => {
  const theme = useTheme();
  const isExpired = user.expirationDate ? moment(user.expirationDate).isBefore(moment()) : false;

  return (
    <SummaryCard
      colorScheme="info"
      sx={{
        p: 2.5,
        animationDelay: "0.1s",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <Button
        onClick={renewUser}
        variant="text"
        size="small"
        startIcon={<PublishedWithChangesIcon sx={{ fontSize: 18 }} />}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          borderRadius: "12px",
          bgcolor: alpha(theme.palette.success.dark, 0.15),
          color: "success.dark",
          textTransform: "none",
          fontWeight: 700,
          px: 1.5,
          py: 0.5,
          fontSize: "0.75rem",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "& .MuiButton-startIcon": {
            mr: 0.5,
          },
          "&:hover": {
            bgcolor: alpha(theme.palette.success.dark, 0.25),
            transform: "translateY(-2px) scale(1.02)",
            boxShadow: `0 4px 12px ${alpha(theme.palette.success.dark, 0.25)}`,
          },
        }}
      >
        Renew
      </Button>
      <Box
        sx={{
          width: 54,
          height: 54,
          borderRadius: "50%",
          bgcolor: alpha(theme.palette.info.main, 0.15),
          color: "info.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.5,
          boxShadow: `0 8px 16px ${alpha(theme.palette.info.main, 0.1)}`,
        }}
      >
        <SyncIcon sx={{ fontSize: 28, animation: `${spin} 10s linear infinite` }} />
      </Box>{" "}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 900,
          color: "info.main",
          letterSpacing: "0.08em",
          mb: 0.5,
          display: "block",
          fontSize: "0.6rem",
        }}
      >
        CURRENT PLAN
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 900,
          mb: 0.2,
          letterSpacing: "-0.01em",
          fontSize: "0.85rem",
          lineHeight: 1.2,
          wordBreak: "break-all",
        }}
      >
        {user.planName || user.plan?.planName || "No Active Plan"}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: isExpired ? "error.main" : "text.secondary",
          mb: 2,
          display: "block",
        }}
      >
        {isExpired ? "Expired:" : "Expires:"}{" "}
        {user.expirationDate ? moment(user.expirationDate).format("MMM DD, YYYY") : "---"}
      </Typography>
      <Box
        sx={{
          width: "100%",
          height: "1px",
          bgcolor: alpha(theme.palette.divider, 0.06),
          mb: 2,
        }}
      />
      <Grid container spacing={2}>
        <Grid item xs={6} sx={{ borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <Stack spacing={0.5} alignItems="center">
            <Typography
              variant="caption"
              sx={{
                fontWeight: 900,
                color: "text.secondary",
                letterSpacing: "0.05em",
                fontSize: "0.6rem",
              }}
            >
              DATA LIMIT
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "text.primary" }}>
              {user.quotaLimit === "0" || user.quotaLimit === 0
                ? "Unlimited"
                : user.quotaLimit
                  ? formatBytes(user.quotaLimit)
                  : user.plan?.totalLimit || "Unlimited"}
            </Typography>
            {user.quotaLimit !== "0" && user.quotaLimit !== 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.6rem", fontWeight: 800, color: "warning.dark" }}
                >
                  Used: {formatBytes(user.usedQuotaLimit || 0)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.6rem", fontWeight: 800, color: "success.main" }}
                >
                  Bal:{" "}
                  {formatBytes(Math.max(0, (user.quotaLimit || 0) - (user.usedQuotaLimit || 0)))}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Grid>

        <Grid item xs={6}>
          <Stack spacing={0.5} alignItems="center">
            <Typography
              variant="caption"
              sx={{
                fontWeight: 900,
                color: "text.secondary",
                letterSpacing: "0.05em",
                fontSize: "0.6rem",
              }}
            >
              VALIDITY
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "text.primary" }}>
              {(() => {
                const val =
                  user.validityStr ||
                  user.plan?.validityStr ||
                  user.validity ||
                  user.planValidity ||
                  user.plan?.planValidity;
                if (!val) return "---";
                const valStr = val.toString().toLowerCase();
                if (valStr.includes("day") || valStr.includes("month") || valStr.includes("year")) {
                  return val;
                }
                return `${val} Days`;
              })()}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: "0.6rem", fontWeight: 800, color: "warning.dark" }}
            >
              Rate: {user.rateName || "---"}
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    </SummaryCard>
  );
};

export const FinancialWidget = ({ user, userDetailsInvoice }) => {
  const theme = useTheme();
  return (
    <SummaryCard
      colorScheme="success"
      sx={{
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <SectionTitle title="Financial Status" icon={PaymentsIcon} color="success" centered />

      <Stack spacing={3} sx={{ width: "100%", alignItems: "center" }}>
        {/* Total Due Section */}
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 900,
              color: "error.main",
              letterSpacing: "0.05em",
              fontSize: "0.65rem",
              display: "block",
              mb: 0.5,
              textTransform: "uppercase",
            }}
          >
            TOTAL DUE
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
              ₹{user?.dueAmount || userDetailsInvoice?.dueAmount || 0}
            </Typography>
            <Box
              sx={{
                px: 1.2,
                py: 0.4,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: "error.main",
                fontSize: "0.6rem",
                fontWeight: 900,
                letterSpacing: "0.02em",
              }}
            >
              UNPAID
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: "40%",
            height: "1px",
            bgcolor: alpha(theme.palette.divider, 0.08),
          }}
        />

        {/* Total Paid Section */}
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 900,
              color: "success.main",
              letterSpacing: "0.05em",
              fontSize: "0.65rem",
              display: "block",
              mb: 0.5,
              textTransform: "uppercase",
            }}
          >
            TOTAL PAID
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
              ₹{user?.paidAmount || userDetailsInvoice?.paidAmount || 0}
            </Typography>
            <Box
              sx={{
                px: 1.2,
                py: 0.4,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: "success.main",
                fontSize: "0.6rem",
                fontWeight: 900,
                letterSpacing: "0.02em",
              }}
            >
              RECEIVED
            </Box>
          </Box>
        </Box>

        {(user?.gstNo || userDetailsInvoice?.gstNo) && (
          <>
            <Box
              sx={{
                width: "40%",
                height: "1px",
                bgcolor: alpha(theme.palette.divider, 0.08),
              }}
            />

            {/* GST No Section */}
            <Box sx={{ width: "100%", textAlign: "center" }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 900,
                  color: "info.main",
                  letterSpacing: "0.05em",
                  fontSize: "0.65rem",
                  display: "block",
                  mb: 0.5,
                  textTransform: "uppercase",
                }}
              >
                GST NUMBER
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "text.primary" }}>
                  {user?.gstNo || userDetailsInvoice?.gstNo}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        <Box
          sx={{
            mt: 1,
            px: 2,
            py: 1,
            borderRadius: "50px",
            bgcolor: alpha(theme.palette.info.main, 0.03),
            border: `1px dashed ${alpha(theme.palette.info.main, 0.25)}`,
            width: "fit-content",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 700,
              fontSize: "0.7rem",
              display: "block",
            }}
          >
            Last payment verified on {moment().format("MMM DD, YYYY")}
          </Typography>
        </Box>
      </Stack>
    </SummaryCard>
  );
};

const UserSummary = ({
  user,
  userDetailsInvoice,
  profileImageUrl,
  handleAvatarClick,
  renewUser,
  permissionKeys,
  operator,
}) => {
  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} md={3.5}>
        <ProfileWidget
          user={user}
          profileImageUrl={profileImageUrl}
          handleAvatarClick={handleAvatarClick}
        />
      </Grid>
      <Grid item xs={12} md={4.5}>
        <PlanWidget
          user={user}
          renewUser={renewUser}
          permissionKeys={permissionKeys}
          operator={operator}
        />
      </Grid>
      <Grid item xs={12} md={3.5}>
        <FinancialWidget user={user} userDetailsInvoice={userDetailsInvoice} />
      </Grid>
    </Grid>
  );
};
export default UserSummary;
